import * as Sentry from '@sentry/react';
import { Integrations as SentryTracingIntegrations } from '@sentry/tracing';

import axios from 'axios';
import { get } from 'lodash';

import * as stackTraceParser from 'stacktrace-parser';

import sentryEndpoint from 'constants/sentryEndpoint';
import {
  PRODUCTION,
  IGNORED_ERRORS,
  SENTRY_DENY_URLS,
  SENTRY_EXCLUDE_ENVIRONMENTS,
  LIBRARY_ERROR_WITH_PATH_SPECIFIED_REGEXES,
} from 'services/constants';
import { ensureArray } from 'utils/arrays';
import { ensureObject } from 'utils/objects';
import { ensureString } from 'utils/string';

/**
 * @param activeEnvironment {string}
 * @returns {boolean}
 */
const isSentryEnabledForEnv = activeEnvironment =>
  activeEnvironment && !SENTRY_EXCLUDE_ENVIRONMENTS.includes(activeEnvironment);

/**
 * @param hint {Object<{originalException: {@message: string | null, @stack: string | null}}>}
 * @returns {boolean}
 */
const checkIfErrorIgnored = hint => {
  const { originalException } = ensureObject(hint);
  const { message, stack } = ensureObject(originalException);
  const firstLineOfStackTrace = ensureArray(
    stackTraceParser.parse(ensureString(stack)),
  )[0];
  const globallyIgnored = IGNORED_ERRORS.some(
    regex =>
      ensureString(message).match(regex) || ensureString(stack).match(regex),
  );

  const fileSpecificIgnored = LIBRARY_ERROR_WITH_PATH_SPECIFIED_REGEXES.some(
    ignoreObject => {
      const match =
        ensureString(firstLineOfStackTrace?.file).match(ignoreObject?.file) &&
        ensureString(message).match(ignoreObject?.message);
      return match;
    },
  );
  return fileSpecificIgnored || globallyIgnored;
};

const initSentry = () => {
  const activeEnvironment = ENVIRONMENT;

  if (!isSentryEnabledForEnv(activeEnvironment)) {
    return;
  }

  const integrations = [new SentryTracingIntegrations.BrowserTracing()];
  const envSpecificConfigurations = {
    prod: {
      release: `sl-fe@${VERSION}`,
      integrations,
      tracesSampleRate: 0,
      // Ignore errors
      // https://stackoverflow.com/questions/55846690/how-to-globally-ignore-errors-with-sentry-v5-to-reduce-noise
      // https://github.com/getsentry/sentry-javascript/blob/ab7ba810a97a2acae3dbd2c82b07e3972147bb97/packages/browser/examples/app.js#L38
      ignoreErrors: IGNORED_ERRORS,
    },
  }?.[activeEnvironment] ?? {
    ignoreErrors: IGNORED_ERRORS,
  };

  Sentry.init({
    dsn: sentryEndpoint,
    debug: false,
    denyUrls: SENTRY_DENY_URLS,
    onunhandledrejection: false,
    environment: activeEnvironment,
    // eslint-disable-next-line complexity
    beforeSend(event, hint) {
      // log warning only on lower environments
      if (activeEnvironment !== PRODUCTION) {
        const errorMessage = get(
          event,
          ['exception', 'values', 0, 'value'],
          '',
        );
        const eventId = get(event, ['event_id'], '');
        console.warn(
          `Event sent to Sentry. ${errorMessage} \n https://sentry.io/schoolinks/sl-fe/events/${eventId} `,
        );
      }

      // Load chunk error caused by network issue
      // since we are not deleting old chunks, the chunk issue is
      // most likely caused by network
      const isLoadingChunkNetworkIssue =
        hint?.originalException?.message?.startsWith('Loading chunk');
      if (isLoadingChunkNetworkIssue) {
        window.location.reload(true);
        return null;
      }

      // Sentry isTrusted error: https://github.com/getsentry/sentry-javascript/issues/3217
      const isSentryIsTrustedError =
        get(event?.exception?.values?.[0]?.value ?? {}, 'isTrusted') === false;

      // Ignored error
      const isIgnored = checkIfErrorIgnored(hint);

      // misc axios error
      const isAxiosCancel = axios.isCancel(hint?.originalException);
      const isAxiosError = hint?.originalException?.isAxiosError;

      // pageHide event google translation error
      const isPageHideGoogleTranslationError =
        get(event?.exception?.values?.[0]?.value ?? {}, 'type') === 'pagehide';

      if (
        isAxiosCancel ||
        isAxiosError ||
        isIgnored ||
        isSentryIsTrustedError ||
        isPageHideGoogleTranslationError
      ) {
        return null;
      }
      return event;
    },
    ...envSpecificConfigurations,
  });
};

export { initSentry, isSentryEnabledForEnv };
